import { Badge, Button, cn } from "@qn/ui";
import {
  Check,
  ChevronDown,
  CircleDashed,
  CircleX,
  FileText,
  Globe,
  ListChecks,
  Loader2,
  ShieldQuestion,
  Terminal,
} from "lucide-react";
import { useState } from "react";
import type { TodoItem } from "../lib/agent-store";
import { useAgents } from "../lib/agents";
import type { PendingCall } from "../lib/api";
import { useT } from "../lib/i18n";
import { Markdown } from "./Markdown";
import { NamedIcon } from "./NamedIcon";

export type ToolState = "running" | "awaiting_approval" | "denied" | "done" | "error";

const ICONS: Record<string, typeof Terminal> = {
  bash: Terminal,
  python: Terminal,
  read_file: FileText,
  write_file: FileText,
  list_files: FileText,
  web_search: Globe,
  web_fetch: Globe,
  todo_write: ListChecks,
};

export function summarize(name: string, input: Record<string, unknown>): string {
  const v =
    input.command ??
    input.query ??
    input.url ??
    input.path ??
    (name === "python" ? String(input.code ?? "").split("\n")[0] : undefined);
  if (name === "todo_write") return `${(input.todos as unknown[] | undefined)?.length ?? 0} items`;
  return v ? String(v).slice(0, 120) : "";
}

export function ToolCard({
  name,
  input,
  state,
  result,
  defaultOpen,
}: {
  name: string;
  input: Record<string, unknown>;
  state: ToolState;
  result?: string;
  defaultOpen?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(!!defaultOpen);
  const Icon = ICONS[name] ?? Terminal;
  const status = {
    running: <Loader2 className="size-4 animate-spin text-[var(--accent-text)]" />,
    awaiting_approval: <ShieldQuestion className="size-4 text-[var(--warn)]" />,
    denied: <CircleX className="size-4 text-[var(--danger)]" />,
    error: <CircleX className="size-4 text-[var(--danger)]" />,
    done: <Check className="size-4 text-[var(--success)]" />,
  }[state];
  const code =
    name === "bash"
      ? String(input.command ?? "")
      : name === "python"
        ? String(input.code ?? "")
        : name === "write_file"
          ? String(input.content ?? "")
          : "";
  return (
    <div
      className="my-2 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface-2)]/60"
      dir="ltr"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-start text-sm"
      >
        <Icon className="size-4 shrink-0 text-[var(--fg-muted)]" />
        <span className="font-mono text-xs font-semibold">{name}</span>
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-[var(--fg-muted)]">
          {summarize(name, input)}
        </span>
        {state === "awaiting_approval" && <Badge tone="warn">{t("agent.needsApproval")}</Badge>}
        {status}
        <ChevronDown
          className={cn("size-3.5 text-[var(--fg-faint)] transition", open && "rotate-180")}
        />
      </button>
      {open && (
        <div className="space-y-2 border-t border-[var(--line)] p-3">
          {code ? (
            <pre className="max-h-64 overflow-auto rounded-lg bg-[var(--surface)] p-2.5 font-mono text-xs leading-relaxed">
              {code}
            </pre>
          ) : (
            <pre className="max-h-40 overflow-auto rounded-lg bg-[var(--surface)] p-2.5 font-mono text-xs">
              {JSON.stringify(input, null, 2)}
            </pre>
          )}
          {result !== undefined && (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-[var(--fg-faint)]">
                {t("agent.result")}
              </div>
              <pre
                className={cn(
                  "max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-[var(--surface)] p-2.5 font-mono text-xs leading-relaxed",
                  state === "error" && "text-[var(--danger)]",
                )}
              >
                {result.replace(/^\[Tool output[^\n]*\n/, "")}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ApprovalBar({
  calls,
  busy,
  onDecide,
}: {
  calls: PendingCall[];
  busy: boolean;
  onDecide: (decisions: Record<string, "approve" | "deny">, always: boolean) => void;
}) {
  const t = useT();
  const [always, setAlways] = useState(false);
  const pending = calls.filter((c) => c.decision === "pending");
  if (!pending.length) return null;
  const all = (d: "approve" | "deny") => Object.fromEntries(pending.map((c) => [c.id, d]));
  return (
    <div
      role="alertdialog"
      aria-label={t("agent.approvalTitle")}
      className="mx-auto mb-2 w-[calc(100%-1.5rem)] max-w-[var(--chat-width)] rounded-2xl border border-[var(--warn)]/50 bg-[color-mix(in_oklab,var(--warn)_8%,var(--surface))] p-3.5 shadow-[var(--shadow)] sm:w-[calc(100%-2rem)]"
    >
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        <ShieldQuestion className="size-4 text-[var(--warn)]" /> {t("agent.approvalTitle")}
      </div>
      <ul className="mb-3 space-y-1.5">
        {pending.map((c) => (
          <li
            key={c.id}
            className="rounded-lg bg-[var(--surface)] px-3 py-2 font-mono text-xs"
            dir="ltr"
          >
            <span className="font-semibold">{c.name}</span>
            {c.input.network === true && (
              <Badge tone="danger" className="ms-2">
                network
              </Badge>
            )}
            <div className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap text-[var(--fg-muted)]">
              {summarize(c.name, c.input) || JSON.stringify(c.input)}
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={busy}
          onClick={() => onDecide(all("approve"), always)}
        >
          {t("agent.approve")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onDecide(all("deny"), false)}
        >
          {t("agent.deny")}
        </Button>
        <label className="ms-auto flex items-center gap-2 text-xs text-[var(--fg-muted)]">
          <input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} />{" "}
          {t("agent.always")}
        </label>
      </div>
    </div>
  );
}

export function TaskPanel({ tasks }: { tasks: TodoItem[] }) {
  const t = useT();
  const [open, setOpen] = useState(true);
  if (!tasks.length) return null;
  const done = tasks.filter((x) => x.status === "completed").length;
  return (
    <div className="mx-auto mb-2 w-full max-w-[var(--chat-width)] px-3 sm:px-4">
      <div className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] shadow-sm">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 px-3.5 py-2.5 text-sm font-medium"
        >
          <ListChecks className="size-4 text-[var(--accent-text)]" /> {t("agent.progress")}
          <span className="text-xs font-normal text-[var(--fg-muted)]">
            {done}/{tasks.length}
          </span>
          <div className="mx-2 h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--surface-3)]">
            <div
              className="h-full rounded-full bg-[var(--accent)] transition-all"
              style={{ width: `${(done / tasks.length) * 100}%` }}
            />
          </div>
          <ChevronDown
            className={cn("size-4 text-[var(--fg-faint)] transition", open && "rotate-180")}
          />
        </button>
        {open && (
          <ul className="space-y-1.5 border-t border-[var(--line)] px-3.5 py-3">
            {tasks.map((x) => (
              <li key={x.id} className="flex items-start gap-2.5 text-sm" dir="auto">
                {x.status === "completed" ? (
                  <Check className="mt-0.5 size-4 shrink-0 text-[var(--success)]" />
                ) : x.status === "in_progress" ? (
                  <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-[var(--accent-text)]" />
                ) : (
                  <CircleDashed className="mt-0.5 size-4 shrink-0 text-[var(--fg-faint)]" />
                )}
                <span
                  className={cn(x.status === "completed" && "text-[var(--fg-muted)] line-through")}
                >
                  {x.content}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** A task handed to a specialist sub-agent: shows who, what, live progress and the final report. */
export function DelegateCard({
  input,
  state,
  result,
  live,
}: {
  input: Record<string, unknown>;
  state: ToolState;
  result?: string;
  live?: { detail?: string; state: string };
}) {
  const t = useT();
  const agents = useAgents().data?.agents ?? [];
  const a = agents.find((x) => x.id === input.agent || x.name === input.agent);
  const [open, setOpen] = useState(false);
  const running = state === "running";
  return (
    <div className="my-2 overflow-hidden rounded-xl border border-[var(--accent-ring)] bg-[var(--accent-soft)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-start"
      >
        <NamedIcon name={a?.icon} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium" dir="auto">
            {a?.name ?? String(input.agent)}
          </span>
          <span className="block truncate text-xs text-[var(--fg-muted)]" dir="auto">
            {running && live?.detail
              ? `${t("agent.using")} ${live.detail}…`
              : String(input.task ?? "").slice(0, 140)}
          </span>
        </span>
        {running ? (
          <Loader2 className="size-4 animate-spin text-[var(--accent-text)]" />
        ) : state === "error" || state === "denied" ? (
          <CircleX className="size-4 text-[var(--danger)]" />
        ) : (
          <Check className="size-4 text-[var(--success)]" />
        )}
        <ChevronDown
          className={cn("size-3.5 text-[var(--fg-faint)] transition", open && "rotate-180")}
        />
      </button>
      {open && (
        <div className="space-y-2 border-t border-[var(--accent-ring)] bg-[var(--surface)] p-3">
          <div className="text-[11px] uppercase tracking-wide text-[var(--fg-faint)]">
            {t("agent.brief")}
          </div>
          <p className="whitespace-pre-wrap text-sm text-[var(--fg-muted)]" dir="auto">
            {String(input.task ?? "")}
          </p>
          {result !== undefined && (
            <>
              <div className="text-[11px] uppercase tracking-wide text-[var(--fg-faint)]">
                {t("agent.report")}
              </div>
              <Markdown text={result.replace(/^\[Tool output[^\n]*\n/, "")} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
